import React, { useState } from 'react';
import Pagination from '../Pagination/Pagination';
import { IoMdArrowDropdown } from "react-icons/io";
import { IoMdArrowDropup } from "react-icons/io";
import Loader from '../Loader/Loader';
import { getEmployeeProfileImage } from '../../utils/employeeProfileImage';

// Utility function to check if user has admin privileges (admin or super role)
const hasAdminPrivileges = (role) => {
  return role === 'admin' || role === 'super';
};

const EMPLOYEE_STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Exited', label: 'Inactive' },
  { value: 'On Leave', label: 'On Leave' },
];

const normalizeEmployeeStatusValue = (status) => {
  if (status === 'Inactive') return 'Exited';
  if (status === 'Active' || status === 'On Leave' || status === 'Exited') {
    return status;
  }
  return 'Active';
};

export default function EmployeeTable({ employees, userProfile, handleEditEmployee, onRowClick, page, limit, total, setPage, setLimit, sortBy, setSortBy, sortOrder, setSortOrder, loading, onRowStatusUpdate }) {
  const showShiftColumn = hasAdminPrivileges(userProfile?.role);
  const canChangeStatus = hasAdminPrivileges(userProfile?.role);
  const [updatingStatusId, setUpdatingStatusId] = useState('');
  const columns = [
    { label: 'Name', sortKey: 'name' },
    { label: 'Employee ID', sortKey: 'employee_no' },
    { label: 'Position' },
    { label: 'Grade' },
    ...(showShiftColumn ? [{ label: 'Shift' }] : []),
    { label: 'Status' },
    ...(canChangeStatus ? [{ label: 'Change Status' }] : []),
  ];

  const handleSort = (newSortBy) => {
    if (!newSortBy) return;

    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('asc');
    }
  };

  const totalPages = Math.ceil(total / limit);

  const handleStatusChange = async (employee, event) => {
    event.stopPropagation();
    if (!onRowStatusUpdate) return;
    const employeeId = employee?.id || employee?.employee_id;
    if (!employeeId) return;
    const nextStatus = normalizeEmployeeStatusValue(event.target.value);
    const currentStatus = normalizeEmployeeStatusValue(employee?.status);
    if (nextStatus === currentStatus) return;

    try {
      setUpdatingStatusId(employeeId);
      await onRowStatusUpdate(employee, nextStatus);
    } catch (error) {
      const detail = error?.response?.data?.detail;
      alert(typeof detail === 'string' ? detail : 'Failed to update employee status.');
    } finally {
      setUpdatingStatusId('');
    }
  };

  return (
    <div className="relative overflow-x-auto border border-gray-200 rounded-lg shadow-sm">
      <table className="min-w-full divide-y divide-gray-200 text-sm text-left">
        <thead className="bg-[#674936]">
          <tr>
            {columns.map((col) => (
              <th key={col.label} className="px-4 py-3 text-white font-semibold cursor-pointer">
                <div className='flex items-center flex-row gap-1 whitespace-nowrap'>
                  {col.label}
                  {col.sortKey && (
                    <span className='mt-1 flex flex-col '>
                      <IoMdArrowDropup
                        className={`-mb-[5px] text-[1rem] cursor-pointer ${sortBy === col.sortKey && sortOrder === 'asc' ? 'opacity-40 cursor-not-allowed' : ''}`}
                        onClick={() => {
                          if (!(sortBy === col.sortKey && sortOrder === 'asc')) handleSort(col.sortKey);
                        }}
                        aria-disabled={sortBy === col.sortKey && sortOrder === 'asc'}
                      />
                      <IoMdArrowDropdown
                        className={`cursor-pointer text-[1rem] ${sortBy === col.sortKey && sortOrder === 'desc' ? 'opacity-40 cursor-not-allowed' : ''}`}
                        onClick={() => {
                          if (!(sortBy === col.sortKey && sortOrder === 'desc')) handleSort(col.sortKey);
                        }}
                        aria-disabled={sortBy === col.sortKey && sortOrder === 'desc'}
                      />
                    </span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {employees.length === 0 && !loading ? (
            <tr>
              <td colSpan={columns.length} className="text-center py-8 text-gray-500">
                No employees found.
              </td>
            </tr>
          ) : employees.map((employee) => {
            const profileImage = getEmployeeProfileImage(employee);
            return (
            <tr key={employee.id} className="bg-white hover:bg-gray-50 cursor-pointer" onClick={() => onRowClick?.(employee)}>
              <td className="px-4 py-3 align-top min-w-0">
                <div className="flex items-start gap-3 min-w-0">
                {profileImage ? (
                  <img
                    className="w-10 h-10 rounded-full shrink-0 object-cover"
                    src={profileImage}
                    alt={employee.name}
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center font-bold text-gray-600 shrink-0">
                    {employee.name
                      .split(' ')
                      .map((n) => n[0])
                      .join('')
                      .slice(0, 2)
                      .toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <div className="font-medium text-gray-900 break-words">{employee.name}</div>
                  <div className="text-gray-500 text-xs break-all">{employee.email}</div>
                </div>
                </div>
              </td>
              <td className="px-4 py-3 whitespace-nowrap">{employee.employee_no}</td>
              <td className="px-4 py-3 min-w-0 max-w-[10rem] sm:max-w-none break-words">{employee.position}</td>
              <td className="px-4 py-3 text-gray-700 min-w-0 max-w-[8rem] break-words">
                {employee.grade_name && employee.grade_name !== '—'
                  ? employee.grade_name
                  : '—'}
              </td>
              {showShiftColumn && (
                <td className="px-4 py-3 text-gray-700 min-w-0 max-w-[8rem] break-words">
                  {employee.shift_name || '—'}
                  {employee.shift_is_default && employee.shift_name && employee.shift_name !== '—' && (
                    <span className="text-gray-500 text-xs block">(default)</span>
                  )}
                </td>
              )}
              <td className="px-4 py-3">
                <span
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${employee.status === 'Active'
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                    }`}
                >
                  <span className="w-2 h-2 mr-1 rounded-full bg-current shrink-0"></span>
                  {employee.status === 'Exited' || employee.status === 'Inactive'
                    ? 'Inactive'
                    : employee.status ?? 'N/A'}
                </span>
              </td>
              {canChangeStatus && (
                <td
                  className="px-4 py-3 whitespace-nowrap"
                  onClick={(e) => e.stopPropagation()}
                >
                  <select
                    value={normalizeEmployeeStatusValue(employee.status)}
                    onChange={(event) => handleStatusChange(employee, event)}
                    disabled={updatingStatusId === (employee.id || employee.employee_id)}
                    className="border border-gray-300 rounded-lg px-3 py-2 text-sm font-semibold text-gray-800 bg-white focus:outline-none focus:border-[#674936] disabled:opacity-50 disabled:cursor-not-allowed min-w-[9rem]"
                    aria-label={`Change status for ${employee.name}`}
                  >
                    {EMPLOYEE_STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </td>
              )}
              {/* {hasAdminPrivileges(userProfile?.role) && (
                <td className="px-4 py-3">
                  <button
                    onClick={(e) => {
                        e.stopPropagation();
                        handleEditEmployee(employee)}
                    } 
                    className="text-blue-600 hover:underline text-sm"
                  >
                    Edit user
                  </button>
                </td>
              )} */}
            </tr>
          );
          })}
        </tbody>
      </table>
      {employees.length !== 0 &&
        <Pagination
        totalPages={totalPages}
        currentPage={page + 1}
        onPageChange={(newPage) => setPage(newPage - 1)}
      />
      }
      {loading && (
        <Loader />
      )}
    </div>
  );
}